import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Anchor,
  Badge,
  Button,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import type { ApplicationAnswers, ApplicationField, ApplicationReviewSide } from "@wwrp/shared";
import { applicationsApi, type ApplicationDetail } from "../api/applications.js";
import { RoleBadge } from "./RoleBadge.js";
import { applicationProgressLabel } from "@wwrp/shared";

export function ApplicationDiscussion({
  application,
  canViewProfiles,
  message,
  onMessageChange,
  onSend,
  pending,
}: {
  application: ApplicationDetail;
  canViewProfiles: boolean;
  message: string;
  onMessageChange: (value: string) => void;
  onSend: () => void;
  pending: boolean;
}) {
  return (
    <Paper withBorder p="lg" radius="md" style={{ minWidth: 0 }}>
      <Stack gap="md">
        <Title order={3}>Discussion</Title>
        <Text size="sm" c="dimmed">
          {application.actions.viewInternalReview
            ? "Replies are visible to the member. Use private staff notes for internal discussion."
            : "Use this discussion to contact the staff reviewing your application."}
        </Text>
        {application.messages.length === 0 && (
          <Text size="sm" c="dimmed">
            No replies yet.
          </Text>
        )}
        {application.messages.map((reply) => (
          <Stack
            key={reply.id}
            gap="xs"
            component="article"
            aria-label={`Reply from ${reply.authorName ?? `Member #${reply.authorId}`}`}
            style={{ minWidth: 0, borderBottom: "1px solid var(--border)", paddingBottom: "1rem" }}
          >
            <Group gap="xs" align="center">
              {canViewProfiles ? (
                <Anchor component={Link} to={`/members/${reply.authorId}`} fw={700} c="inherit">
                  {reply.authorName ?? `Member #${reply.authorId}`}
                </Anchor>
              ) : (
                <Text fw={700} c="inherit">
                  {reply.authorName ?? `Member #${reply.authorId}`}
                </Text>
              )}
              {reply.authorRole && reply.authorRole !== "applicant" && (
                <RoleBadge role={reply.authorRole} />
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {reply.authorDepartmentName ? `${reply.authorDepartmentName} · ` : ""}
              {new Date(reply.createdAt).toLocaleString()}
            </Text>
            <Text
              size="sm"
              style={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                lineHeight: 1.7,
                maxWidth: "80ch",
              }}
            >
              {reply.body}
            </Text>
          </Stack>
        ))}
        {application.actions.reply ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (message.trim() && !pending) onSend();
            }}
          >
            <Stack gap="sm">
              <Textarea
                label="Reply"
                description="Visible to the member and staff reviewing this application."
                minRows={3}
                autosize
                maxLength={5000}
                value={message}
                onChange={(event) => onMessageChange(event.currentTarget.value)}
                disabled={pending}
              />
              <Group justify="flex-end">
                <Button type="submit" disabled={!message.trim() || pending} loading={pending}>
                  Send reply
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Text size="sm" c="dimmed">
            This discussion is read-only.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

export function ApplicationActivityLog({
  application,
  canViewProfiles,
}: {
  application: ApplicationDetail;
  canViewProfiles: boolean;
}) {
  if (!application.activity?.length) return null;
  return (
    <Paper withBorder p="lg" radius="md">
      <Stack gap="sm">
        <Title order={4}>Activity</Title>
        {application.activity.map((entry) => (
          <Stack key={entry.id} gap={2}>
            <Text size="sm" style={{ overflowWrap: "anywhere" }}>
              {entry.actorName && (
                <>
                  {canViewProfiles && entry.actorId ? (
                    <Anchor component={Link} to={`/members/${entry.actorId}`} c="inherit" fw={600}>
                      {entry.actorName}
                    </Anchor>
                  ) : (
                    <b>{entry.actorName}</b>
                  )}{" "}
                </>
              )}
              {entry.type === "submitted"
                ? "submitted the application"
                : entry.type === "withdrawn"
                  ? "withdrew the application"
                  : entry.type === "closed"
                    ? "closed the application"
                    : `${entry.type} ${entry.departmentName ?? "the department"} review`}
            </Text>
            <Text size="xs" c="dimmed">
              {new Date(entry.createdAt).toLocaleString()}
            </Text>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

export function ApplicationApplicantSummary({
  application,
  canViewProfile,
  canViewConduct,
  memberView = false,
}: {
  application: ApplicationDetail;
  canViewProfile: boolean;
  canViewConduct: boolean;
  memberView?: boolean;
}) {
  const name = application.applicantName ?? `Member #${application.applicantId}`;
  if (memberView)
    return (
      <Paper withBorder p="lg" radius="md">
        <Stack gap="sm">
          <Text fw={600}>{applicationProgressLabel(application)}</Text>
          <Text size="sm" c="dimmed">
            Submitted {new Date(application.createdAt).toLocaleString()}
          </Text>
          {application.type === "transfer" && (
            <Text size="sm">
              {application.sourceDepartmentName} → {application.targetDepartmentName}
            </Text>
          )}
          {application.reviews
            .filter((review) => review.round === application.reviewRound && review.stipulations)
            .map((review) => (
              <Text
                key={review.id}
                size="sm"
                style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {review.stipulations}
              </Text>
            ))}
        </Stack>
      </Paper>
    );
  return (
    <Paper withBorder p="lg" radius="md" style={{ minWidth: 0 }}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Text size="sm">
              Submitted by{" "}
              {canViewProfile ? (
                <Anchor component={Link} to={`/members/${application.applicantId}`} fw={700}>
                  {name}
                </Anchor>
              ) : (
                <b>{name}</b>
              )}
            </Text>
            <Text size="xs" c="dimmed">
              {application.applicantWebsiteId
                ? `Website ID ${application.applicantWebsiteId} · `
                : ""}
              {new Date(application.createdAt).toLocaleString()}
            </Text>
          </Stack>
          {canViewProfile && (
            <Group gap="xs">
              <Button
                component={Link}
                to={`/members/${application.applicantId}`}
                variant="light"
                size="xs"
              >
                View profile
              </Button>
              {canViewConduct && (
                <Button
                  component={Link}
                  to={`/members/${application.applicantId}?tab=conduct`}
                  variant="light"
                  size="xs"
                >
                  View DAs &amp; conduct
                </Button>
              )}
            </Group>
          )}
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              {application.type === "reserve" ? "Current primary department" : "Transferring from"}
            </Text>
            <Text fw={600} style={{ overflowWrap: "anywhere" }}>
              {application.sourceDepartmentName}
            </Text>
          </Stack>
          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              {application.type === "reserve" ? "Applying to reserve in" : "Transferring to"}
            </Text>
            <Text fw={600} style={{ overflowWrap: "anywhere" }}>
              {application.targetDepartmentName}
            </Text>
          </Stack>
        </SimpleGrid>
        <Text size="sm" c="dimmed">
          {application.type === "reserve"
            ? `Reserve placement in ${application.targetDepartmentName}. The primary department remains ${application.sourceDepartmentName}; only ${application.targetDepartmentName} needs to approve.`
            : `Transfer from ${application.sourceDepartmentName} to ${application.targetDepartmentName}. Both departments must approve.`}
        </Text>
      </Stack>
    </Paper>
  );
}

export function ApplicationAnswersView({
  fields,
  answers,
}: {
  fields: ApplicationField[];
  answers: ApplicationAnswers;
}) {
  return (
    <Stack gap="md" style={{ minWidth: 0 }}>
      {fields.map((field, index) => {
        if (field.type === "section_header")
          return (
            <Title key={field.id} order={3} style={{ overflowWrap: "anywhere" }}>
              {field.label}
            </Title>
          );
        const value = answers[field.id]?.value;
        const answer = Array.isArray(value)
          ? value.join("\n")
          : value === undefined || value === ""
            ? "No answer provided."
            : String(value);
        return (
          <Paper
            key={field.id}
            withBorder
            p="lg"
            radius="md"
            component="section"
            aria-labelledby={`application-question-${field.id}`}
            style={{ minWidth: 0 }}
          >
            <Stack gap="sm" style={{ minWidth: 0 }}>
              <Text size="xs" c="dimmed">
                Question {index + 1}
              </Text>
              <Text
                id={`application-question-${field.id}`}
                fw={700}
                size="md"
                style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap", maxWidth: "80ch" }}
              >
                {field.label}
              </Text>
              {field.description && (
                <Text
                  size="sm"
                  c="dimmed"
                  style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
                >
                  {field.description}
                </Text>
              )}
              <Text size="xs" c="dimmed">
                Answer
              </Text>
              <Text
                size="sm"
                style={{
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  lineHeight: 1.7,
                  maxWidth: "80ch",
                }}
              >
                {answer}
              </Text>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

export function ApplicationReviewSection({
  application,
  pending,
  onAction,
}: {
  application: ApplicationDetail;
  pending: boolean;
  onAction: (action: () => Promise<unknown>) => void;
}) {
  if (!application.actions.viewInternalReview) return null;
  return (
    <>
      <Title order={2}>Department decisions</Title>
      <SimpleGrid cols={{ base: 1, md: application.type === "transfer" ? 2 : 1 }} spacing="md">
        {(["source_department", "target_department"] as const)
          .filter((side) => application.type === "transfer" || side === "target_department")
          .map((side) => (
            <ApplicationDepartmentReview
              key={`${application.id}:${application.reviewRound}:${side}`}
              application={application}
              side={side}
              pending={pending}
              onAction={onAction}
            />
          ))}
      </SimpleGrid>
    </>
  );
}

export function ApplicationDepartmentReview({
  application,
  side,
  pending,
  onAction,
}: {
  application: ApplicationDetail;
  side: ApplicationReviewSide;
  pending: boolean;
  onAction: (action: () => Promise<unknown>) => void;
}) {
  const [note, setNote] = useState("");
  const [stipulations, setStipulations] = useState("");
  const source = side === "source_department";
  const review = application.reviews.find(
    (item) => item.side === side && item.round === application.reviewRound,
  );
  const canDecide = application.actions[source ? "sourceDecision" : "targetDecision"];
  const decide = (decision: "approved" | "denied") =>
    onAction(() =>
      applicationsApi.decision(application.id, {
        side,
        round: application.reviewRound,
        decision,
        note: note.trim(),
        stipulations: stipulations.trim(),
      }),
    );
  return (
    <Paper withBorder p="lg" radius="md" style={{ minWidth: 0 }}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Title order={3}>{source ? "Current department" : "Receiving department"}</Title>
            <Text size="sm" c="dimmed" style={{ overflowWrap: "anywhere" }}>
              {source ? application.sourceDepartmentName : application.targetDepartmentName}
            </Text>
          </Stack>
          <Badge
            variant="light"
            color={
              review?.decision === "approved"
                ? "teal"
                : review?.decision === "denied"
                  ? "red"
                  : "yellow"
            }
          >
            {review?.decision ?? "Awaiting review"}
          </Badge>
        </Group>
        {review && (
          <Text size="xs" c="dimmed">
            Reviewed by {review.reviewerName ?? `Member #${review.reviewerId}`} ·{" "}
            {new Date(review.createdAt).toLocaleString()}
          </Text>
        )}
        {review?.note && application.actions.privateNote && (
          <Stack gap={4}>
            <Text size="sm" fw={700}>
              Staff decision note — hidden from member
            </Text>
            <Text size="sm" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {review.note}
            </Text>
          </Stack>
        )}
        {review?.stipulations && (
          <Stack gap={4}>
            <Text size="sm" fw={700}>
              Stipulations
            </Text>
            <Text size="sm" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {review.stipulations}
            </Text>
          </Stack>
        )}
        {!canDecide && !review && (
          <Text size="sm" c="dimmed" style={{ overflowWrap: "anywhere" }}>
            {application.decisionUnavailableReasons?.[side] ??
              (application.status === "pending"
                ? "Approval and denial are available to eligible department staff. Self-review requires Senior Administration or above."
                : "This application is not awaiting a decision.")}
          </Text>
        )}
        {canDecide && (
          <>
            <Textarea
              label="Staff decision note"
              description="Hidden from the member. Required when denying an application."
              minRows={3}
              autosize
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
              disabled={pending}
            />
            <Textarea
              label="Stipulations (optional)"
              description="These conditions are visible to the member."
              minRows={2}
              autosize
              value={stipulations}
              onChange={(event) => setStipulations(event.currentTarget.value)}
              disabled={pending}
            />
            <Group justify="flex-end" wrap="wrap">
              <Button
                variant="default"
                color="red"
                disabled={pending || !note.trim()}
                onClick={() => decide("denied")}
              >
                Deny
              </Button>
              <Button color="teal" disabled={pending} onClick={() => decide("approved")}>
                Approve
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Paper>
  );
}
